/* eslint-disable no-underscore-dangle */
import { ReactNode, useMemo } from 'react';
import { BLOCKS, INLINES, Node } from '@contentful/rich-text-types';
import { DocumentLinks, Theme } from '@stadionhq-v3/core';
import { Locale } from '@core/constants/locales.constants';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import templateConfig from '@templateConfig';
import { PAGE_CONTENT_TYPES } from '@core/constants/contentTypes.constants';
import { fullPathToArray } from '@utils/strings.utils';
import {
  renderDocumentEmbed,
  EmbedEntry,
  EntryHyperlink,
  EmbedDictionary,
} from './renderDocumentEmbed';
import { renderCommonElements } from './renderCommonElements';
import styles from './useContentfulRichTextRendererOptions.module.scss';

export const useContentfulRichTextRendererOptions = ({
  locale,
  links,
  theme,
  onDark,
  dictionary,
  packagePageLinkPath,
  compact,
}: {
  locale?: Locale;
  links?: DocumentLinks;
  theme?: Theme;
  onDark?: boolean;
  dictionary?: EmbedDictionary;
  packagePageLinkPath?: string;
  compact?: boolean;
}) => {
  const entries = useMemo(() => {
    const entryMap = new Map<string, EmbedEntry | EntryHyperlink>();

    if (links?.entries?.block) {
      (links.entries.block as EmbedEntry[]).forEach((entry) =>
        entryMap.set(entry?.id as string, entry)
      );
    }

    if (links?.entries?.hyperlink) {
      (links.entries.hyperlink as unknown as EntryHyperlink[]).forEach(
        (entry: EntryHyperlink) => entryMap.set(entry?.id, entry)
      );
    }

    return entryMap;
  }, [links?.entries]);

  // TODO
  // Not used yet
  // const assets = useMemo(() => {
  //   const assetMap = new Map();

  //   if (links?.assets?.block) {
  //     links.assets.block.forEach((entry: any) =>
  //       assetMap.set(entry?.id, entry)
  //     );
  //   }
  //   return assetMap;
  // }, [links?.assets]);

  return {
    renderNode: {
      ...renderCommonElements(theme, onDark),
      [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
        const { data } = node;
        const entry = entries.get(data.target.id);
        if (entry) {
          return renderDocumentEmbed(
            entry as EmbedEntry,
            dictionary,
            onDark,
            packagePageLinkPath,
            compact
          );
        }
        return <></>;
      },

      [INLINES.ENTRY_HYPERLINK]: (node: Node, children: ReactNode) => {
        const { data } = node;
        const entry = entries.get(data?.target?.sys?.id) as {
          __typename: string;
          slug: string;
          fullPath?: string;
          id: string;
          parentPage?: {
            slug: string;
          };
          team?: {
            slug: string;
          };
          associatedTeamsCollection?: {
            items: [
              {
                slug: string;
              },
            ];
          };
        };

        const generateHyperlinkEntryHref = (__typename: string) => {
          switch (__typename) {
            case PAGE_CONTENT_TYPES.ARTICLE_PAGE:
              return generateFullPathname({
                locale: locale as Locale,
                route: templateConfig.navigation.routes.ARTICLE,
                params: {
                  article: entry?.slug,
                },
              });
            case PAGE_CONTENT_TYPES.CONTENT_PAGE:
              return generateFullPathname({
                locale: locale as Locale,
                route: templateConfig.navigation.routes.CONTENT,
                params: {
                  content: entry?.fullPath && fullPathToArray(entry?.fullPath),
                },
              });
            case PAGE_CONTENT_TYPES.FIXTURE_PAGE:
              return generateFullPathname({
                locale: locale as Locale,
                route: templateConfig.navigation.routes.FIXTURES,
                params: {
                  team: entry.team?.slug,
                  fixture: entry?.slug,
                },
              });
            case PAGE_CONTENT_TYPES.PROFILE_PAGE: {
              const team = entry.associatedTeamsCollection?.items[0].slug;
              if (team) {
                return generateFullPathname({
                  locale: locale as Locale,
                  route: templateConfig.navigation.routes.PROFILE,
                  params: {
                    team,
                    profile: entry?.slug,
                  },
                });
              }
              return '/';
            }
            case PAGE_CONTENT_TYPES.NEWS_LISTING_PAGE: {
              return generateFullPathname({
                locale: locale as Locale,
                route: templateConfig.navigation.routes.NEWS_LISTING,
                params: {
                  slug: entry?.slug,
                },
              });
            }
            default:
              return '/';
          }
        };

        if (children && entry?.slug) {
          return (
            <a
              className={styles.link}
              href={generateHyperlinkEntryHref(entry?.__typename)}
            >
              {children?.toString()}
            </a>
          );
        }
        return <></>;
      },
    },
  };
};

export default useContentfulRichTextRendererOptions;
